import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { orgCatalogService } from '../services/orgCatalogService';
import { ArrowLeft, Palette, Loader2, Building2, Crown } from 'lucide-react';
import toast from 'react-hot-toast';

const cleanErr = (err) => (err?.message || 'Error').replace('Firebase: ', '').replace(/\s*\(.*\)$/, '');

const Toggle = ({ on, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center w-12 h-7 rounded-full transition-colors flex-shrink-0 disabled:opacity-50 ${on ? 'bg-kumia-green' : 'bg-surface-5'}`}
  >
    <span className={`inline-block w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

/**
 * Branding compartido (Ola 2, modelo "principal = fuente"):
 * El logo, colores y slogan los define la SEDE PRINCIPAL en su propia configuración.
 * Esta pantalla solo decide qué SUCURSALES heredan ese branding:
 *   - Conectada → hereda el branding de la principal (y sus cambios).
 *   - Independiente → gestiona su propio branding.
 * Las fotos del local (slider) son siempre propias de cada sede.
 */
const OrgBranding = () => {
  const navigate = useNavigate();
  const { isGroupOwner, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [sedes, setSedes] = useState([]);

  useEffect(() => {
    if (!authLoading && !isGroupOwner) navigate('/chain', { replace: true });
  }, [authLoading, isGroupOwner, navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const locs = await orgCatalogService.getMyLocations();
      setSedes(locs?.locations || []);
    } catch (err) {
      console.error('[OrgBranding] load error:', err);
      toast.error('No se pudieron cargar las sedes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isGroupOwner) load(); }, [isGroupOwner, load]);

  const primary = useMemo(() => sedes.find((s) => s.isPrimaryLocation), [sedes]);
  const branches = useMemo(() => sedes.filter((s) => !s.isPrimaryLocation), [sedes]);

  const toggleConnected = async (sede) => {
    const connectedNow = sede.catalogOverrides?.branding !== true;
    // conectada → independiente = override true ; independiente → conectada = override false
    setBusyId(sede.id);
    try {
      await orgCatalogService.setLocationOverride({ restaurantId: sede.id, domain: 'branding', override: connectedNow });
      setSedes((prev) => prev.map((s) => s.id === sede.id
        ? { ...s, catalogOverrides: { ...(s.catalogOverrides || {}), branding: connectedNow } }
        : s));
      toast.success(connectedNow
        ? 'Sucursal desconectada: gestionará su propio branding.'
        : 'Sucursal conectada: heredará el branding de la principal.');
    } catch (err) {
      toast.error(cleanErr(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-base via-surface-app to-surface-3">
      {/* Header */}
      <header className="border-b border-line-subtle bg-surface-chrome/80 dark:bg-black/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/chain')} className="text-content-subtle hover:text-content-primary p-1.5 rounded-lg hover:bg-surface-3 flex-shrink-0"><ArrowLeft size={20} /></button>
          <Palette size={18} className="text-kumia-green flex-shrink-0" />
          <h1 className="text-lg font-bold text-content-primary truncate">Branding · Sucursales</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-kumia-green" /></div>
        ) : (
          <div className="space-y-6">
            {/* Explicación */}
            <div className="bg-kumia-green/10 border border-kumia-green/30 rounded-xl p-4 text-sm text-content-secondary">
              El <strong className="text-content-primary">logo, colores y slogan</strong> los define la{' '}
              <strong className="text-content-primary">sede principal</strong> desde su propia configuración.
              Acá decidís qué sucursales los <strong className="text-content-primary">heredan</strong>.
              <span className="block mt-1 text-content-subtle">Las fotos del local (slider) son siempre propias de cada sede.</span>
            </div>

            {/* Fuente (principal) */}
            {primary && (
              <div className="flex items-center gap-2 p-4 rounded-xl bg-surface-3/40 border border-line-strong/50">
                <Crown size={16} className="text-kumia-green flex-shrink-0" />
                <span className="text-sm text-content-primary font-medium truncate">{primary.locationName || primary.name}</span>
                <span className="ml-auto text-xs px-2 py-1 rounded-full bg-kumia-green/15 border border-kumia-green/40 text-kumia-green flex-shrink-0">Fuente del branding</span>
              </div>
            )}

            {/* Sucursales */}
            <div>
              <h2 className="text-content-primary font-semibold mb-1">Sucursales</h2>
              <p className="text-content-subtle text-sm mb-4">
                <strong>Conectada</strong>: hereda el branding de la principal. <strong>Independiente</strong>: gestiona el suyo.
              </p>

              {branches.length === 0 ? (
                <div className="text-center py-10 bg-surface-3/30 rounded-xl border border-line-strong/40">
                  <Building2 size={32} className="mx-auto text-content-subtle mb-2" />
                  <p className="text-content-muted text-sm">No hay sucursales todavía.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {branches.map((sede) => {
                    const connected = sede.catalogOverrides?.branding !== true;
                    const busy = busyId === sede.id;
                    return (
                      <div key={sede.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-surface-3/40 border border-line-strong/40">
                        <div className="flex items-center gap-2 min-w-0">
                          <Building2 size={15} className="text-content-subtle flex-shrink-0" />
                          <div className="min-w-0">
                            <span className="text-sm text-content-primary truncate block">{sede.locationName || sede.name}</span>
                            <span className={`text-xs ${connected ? 'text-kumia-green' : 'text-content-subtle'}`}>
                              {connected ? 'Hereda el branding de la principal' : 'Independiente'}
                            </span>
                          </div>
                        </div>
                        <Toggle on={connected} onClick={() => toggleConnected(sede)} disabled={busy} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgBranding;
